import React, {useEffect, useState} from "react";
import {db} from "../firebase";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  updateDoc,
} from "firebase/firestore";
import {GridColDef, GridRowClassNameParams, GridValidRowModel} from "@mui/x-data-grid";
import {StyledGrid} from "../style/styled-grid";
import {ToggleOff, ToggleOn} from "@mui/icons-material";

function formatPhoneNumber(text: string): string {
  const isSeoulNZ = text.slice(0, 2) === "02";
  const nationZoneRgex = isSeoulNZ ? "(\\d{1,2})" : "(\\d{1,3})";
  const phonePattern = new RegExp(`^${nationZoneRgex}(\\d{0,4})(\\d{0,4}).*$`);

  return text
    .replace(/[^0-9]/g, "")
    .replace(phonePattern, "$1-$2-$3")
    .replace(/^-/, "")
    .replace(/-$/, "")
    .replace(/-$/, "");
}

interface Inquiry {
  id: string;
  phone: string;
  content: string;
  createdAt: number;
  checked: boolean;
  memo: string;
}

const InquiryTable: React.FC = () => {
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    const fetchInquiries = async () => {
      try {
        const inquiryCollection = collection(db, "realtime_inquiry");
        const q = query(inquiryCollection, orderBy("createdAt", "desc"));
        const querySnapshot = await getDocs(q);
        const inquiryList: Inquiry[] = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Inquiry[];
        setInquiries(inquiryList);
      } catch (error) {
        console.error("데이터 가져오기 실패:", error);
      }
    };

    fetchInquiries();
  }, []);

  const processRowUpdate: (
    newRow: GridValidRowModel,
    oldRow: GridValidRowModel
  ) => Promise<GridValidRowModel> = async (newRow, oldRow) => {
    const updatedRow = newRow as Inquiry;
    try {
      const inquiryDoc = doc(db, "realtime_inquiry", updatedRow.id);
      await updateDoc(inquiryDoc, {memo: updatedRow.memo});
      setInquiries((prev) =>
        prev.map((row) => (row.id === updatedRow.id ? updatedRow : row))
      );
      return updatedRow;
    } catch (error) {
      console.error("메모 업데이트 중 오류 발생:", error);
      return oldRow;
    }
  };

  const toggleChecked = async (id: string, currentChecked: boolean) => {
    try {
      const inquiryDoc = doc(db, "realtime_inquiry", id);
      await updateDoc(inquiryDoc, {checked: !currentChecked});
      setInquiries((prev) =>
        prev.map((row) =>
          row.id === id ? {...row, checked: !currentChecked} : row
        )
      );
    } catch (error) {
      console.error("확인 여부 변경 중 오류 발생:", error);
    }
  };

  const columns: GridColDef[] = [
    {field: "id", headerName: "문의 ID", flex: 1},
    {
      field: "createdAt",
      headerName: "생성 시간",
      minWidth: 140,
      valueFormatter: (value) => {
        const date = new Date(value as number);
        const formattedDate = new Intl.DateTimeFormat("ko-KR", {
          year: "2-digit",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }).format(date);
        return formattedDate.replace(/\./g, "").replace(/ /g, ". ");
      },
    },
    {
      field: "phone",
      headerName: "전화번호",
      flex: 1,
      renderCell: (params) => (
        <a
          href={`tel:${params.value}`}
          style={{textDecoration: "none", color: "inherit", cursor: "pointer"}}
        >
          {formatPhoneNumber(params.value as string)}
        </a>
      ),
    },
    {field: "content", headerName: "문의 내용", flex: 2},
    {
      field: "memo",
      headerName: "관리자 메모(더블클릭해 입력)",
      flex: 2,
      editable: true,
    },
    {
      field: "checked",
      headerName: "확인 여부",
      flex: 1,
      renderCell: (params) => (
        <span
          style={{
            color: params.value ? "#AAAAAA" : "inherit",
            cursor: "pointer",
          }}
          onClick={() => toggleChecked(params.row.id, params.value as boolean)}
        >
          {params.value ?
            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px'}}>
              <ToggleOn sx={{width: 24, height: 24, color: '#83A7DD'}}/> 확인됨</div> :
            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px'}}>
              <ToggleOff sx={{width: 24, height: 24, color: '#CCCCCC'}}/> 미확인</div>
          }
        </span>
      ),
    },
  ];

  const getRowClassName = (params: GridRowClassNameParams) => {
    return params.row.checked ? 'checked' : '';
  };

  return (
    <div style={{height: 600, width: "100%", display: "flex", flexDirection: "column", alignItems: "center"}}>
      <StyledGrid
        rows={inquiries}
        columns={columns}
        getRowId={(row) => row.id}
        getRowClassName={getRowClassName}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 20, 50]}
        processRowUpdate={processRowUpdate}
      />
    </div>
  );
};

export default InquiryTable;
